import { createTheme } from "@mui/material/styles";

const mybotgpTheme = createTheme({
  palette: {
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
    },
    primary: {
      main: "#4f8fd9",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#C9C9C9",
    },
  },
  typography: {
    fontFamily: [
      "ui-sans-serif",
      "-apple-system",
      "system-ui",
      "Segoe UI",
      "Helvetica",
      "Apple Color Emoji",
      "Arial",
      "sans-serif",
      "Segoe UI Emoji",
      "Segoe UI Symbol",
    ].join(","),
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
      fontSize: "1.1rem",
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 600,
    },
  },

  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#f0eeff",
          borderRight: "1px solid #d4ccf7",
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&.Mui-selected": {
            backgroundColor: "#4f8fd9",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#4f8fd9",
            },
          },
          "&:hover": {
            backgroundColor: "#e8f3ff",
            cursor: "pointer",
          },
        },
      },
    },

    // TextField compact variant
    MuiTextField: {
      variants: [
        {
          props: { variant: "dense" },
          style: {
            "& .MuiInputBase-root": {
              fontSize: "0.75rem",
              height: "26px",
            },
            "& .MuiInputBase-input": {
              padding: "2px 6px",
            },
          },
        },
      ],
    },

    // Select compact variant
    MuiSelect: {
      variants: [
        {
          props: { variant: "dense" },
          style: {
            fontSize: "0.75rem",
            height: "26px",
            "& .MuiSelect-select": {
              padding: "2px 6px",
            },
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
      variants: [
        // --- dense: base (neutral) ---
        {
          props: { variant: "dense" },
          style: {
            fontSize: "0.72rem",
            fontWeight: 600,
            padding: "4px 12px",
            minHeight: "30px",
            lineHeight: 1.3,
            borderRadius: "8px",
            border: "1px solid rgba(0,0,0,0.12)",
            backgroundColor: "#f7f8fa",
            color: "#333333",
            boxShadow: "none",
            transition:
              "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              backgroundColor: "rgba(79,143,217,0.10)",
              borderColor: "#4f8fd9",
              boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            },
            "&:active": {
              backgroundColor: "rgba(79,143,217,0.20)",
              borderColor: "#24476b",
            },
            "&.Mui-disabled": {
              backgroundColor: "#f7f8fa",
              borderColor: "rgba(0,0,0,0.06)",
              color: "rgba(0,0,0,0.26)",
            },
          },
        },
        // --- dense + primary: filled, for the main/confirm action ---
        {
          props: { variant: "dense", color: "primary" },
          style: {
            backgroundColor: "#4f8fd9",
            borderColor: "#4f8fd9",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#3f78bd",
              borderColor: "#3f78bd",
              boxShadow: "0 2px 4px rgba(79,143,217,0.35)",
            },
            "&:active": {
              backgroundColor: "#2d6cb5",
              borderColor: "#2d6cb5",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(79,143,217,0.35)",
              borderColor: "rgba(79,143,217,0.35)",
              color: "rgba(255,255,255,0.7)",
            },
          },
        },
        // --- dense + secondary: soft grey, for lower-emphasis actions ---
        {
          props: { variant: "dense", color: "secondary" },
          style: {
            backgroundColor: "#eef0f2",
            borderColor: "#d3d6da",
            color: "#4a4a4a",
            "&:hover": {
              backgroundColor: "#e2e5e9",
              borderColor: "#b9bdc2",
            },
            "&:active": {
              backgroundColor: "#d3d6da",
            },
          },
        },
        // --- dense + error: for destructive actions like Delete ---
        {
          props: { variant: "dense", color: "error" },
          style: {
            backgroundColor: "#fdf1f1",
            borderColor: "#f3c8c8",
            color: "#b23b3b",
            "&:hover": {
              backgroundColor: "#fbe3e3",
              borderColor: "#e08a8a",
              boxShadow: "0 1px 2px rgba(178,59,59,0.15)",
            },
            "&:active": {
              backgroundColor: "#f6cccc",
              borderColor: "#c85a5a",
            },
            "&.Mui-disabled": {
              backgroundColor: "#fdf1f1",
              borderColor: "rgba(0,0,0,0.06)",
              color: "rgba(0,0,0,0.26)",
            },
          },
        },
        // --- superdense: compact icon/tag-style button ---
        {
          props: { variant: "superdense" },
          style: {
            fontSize: "0.7rem",
            fontWeight: 600,
            padding: "1px 1px",
            minHeight: "26px",
            minWidth: "40px",
            maxWidth: "40px",
            lineHeight: "1",
            borderRadius: "6px",
            border: "1px solid rgba(0,0,0,0.12)",
            backgroundColor: "#f7f8fa",
            color: "#333333",
            boxShadow: "none",
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": {
              backgroundColor: "rgba(79,143,217,0.10)",
              borderColor: "#4f8fd9",
            },
            "&:active": {
              backgroundColor: "rgba(79,143,217,0.20)",
              borderColor: "#24476b",
            },
          },
        },
      ],
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          backgroundColor: "#E6F7FC",

          "& .MuiDataGrid-row.even": { backgroundColor: "#f9f9f9" },
          "& .MuiDataGrid-row.odd": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#e3f2fd" },

          // Green cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-green": {
            backgroundColor: "#e5f6ea",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-green": {
            backgroundColor: "#d4f7dc",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-green": {
            backgroundColor: "#8fe8a4",
          },
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-lgreen": {
            backgroundColor: "#e3eafb",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-lgreen": {
            backgroundColor: "#fffff",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-lgreen": {
            backgroundColor: "#4f8fd9",
          },
          // Green cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-blue-row": {
            backgroundColor: "#e3eafb",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-blue-row": {
            backgroundColor: "#ffffff",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-blue-row": {
            backgroundColor: "#4f8fd9",
          },

          // Orange cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-orange": {
            backgroundColor: "#ffeacc",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-orange": {
            backgroundColor: "#fff4e5",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-orange": {
            backgroundColor: "#ffe0b2",
          },

          // Pink cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-pink": {
            backgroundColor: "#f8bbd0",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-pink": {
            backgroundColor: "#fce4ec",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-pink": {
            backgroundColor: "#f48fb1",
          },

          // Purple cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-purple": {
            backgroundColor: "#d1c4e9",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-purple": {
            backgroundColor: "#ede7f6",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-purple": {
            backgroundColor: "#b39ddb",
          },

          // Blue cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-blue": {
            backgroundColor: "#d0e8ff",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-blue": {
            backgroundColor: "#e3f2fd",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-blue": {
            backgroundColor: "#bbdefb",
          },

          // Yellow cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-yellow": {
            backgroundColor: "#fff9c4",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-yellow": {
            backgroundColor: "#fffde7",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-yellow": {
            backgroundColor: "#fff59d",
          },

          // Light cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-light": {
            backgroundColor: "#f5f5f5",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-light": {
            backgroundColor: "#fafafa",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-light": {
            backgroundColor: "#eeeeee",
          },

          // Grey cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-grey": {
            backgroundColor: "#ffffff",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-grey": {
            backgroundColor: "#eeeeee",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-grey": {
            backgroundColor: "#bdbdbd",
          },
        },

        columnHeaders: {
          "--DataGrid-t-header-background-base": "#4DA5E2",
          backgroundColor: "#4DA5E2",
          color: "#FFFFFF",
          fontWeight: 700,
          height: 25,
          minHeight: 25,
          maxHeight: 25,
        },
      },

      variants: [
        {
          props: { variant: "superdense" },
          style: {
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.8rem",
              minHeight: "28px !important",
              maxHeight: "28px !important",
              lineHeight: "28px !important",
              marginBottom: "12px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              padding: "0 4px",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.8rem",
              padding: "0 2px",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f9f9f9",
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#f0f0f0",
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#e6e6e6" },
            "& .MuiDataGrid-virtualScroller": { backgroundColor: "#ffffff" },
          },
        },
        {
          props: { variant: "dense" },
          style: {
            "& .MuiDataGrid-columnHeader": { padding: "0 4px" },
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.75rem",
              minHeight: "28px !important",
              maxHeight: "28px !important",
              lineHeight: "28px !important",
              marginBottom: "20px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              padding: "0px",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.75rem",
              padding: "0 4px",
              display: "flex",
              alignItems: "center",
              height: "26px",
            },
            "& .MuiDataGrid-row": {
              minHeight: "26px !important",
              maxHeight: "26px !important",
            },

            "& .MuiDataGrid-row.even.bg-green": { backgroundColor: "#e5f6ea" },
            "& .MuiDataGrid-row.odd.bg-green": { backgroundColor: "#d4f7dc" },
            "& .MuiDataGrid-row.bg-green:hover": { backgroundColor: "#8fe8a4" },
            "& .MuiDataGrid-row.even.bg-lgreen": { backgroundColor: "#d9f0df" },
            "& .MuiDataGrid-row.odd.bg-lgreen": { backgroundColor: "#e6f4ea" },
            "& .MuiDataGrid-row.bg-lgreen:hover": {
              backgroundColor: "#c8e6c9",
            },
            "& .MuiDataGrid-row.even.bg-orange": { backgroundColor: "#ffeacc" },
            "& .MuiDataGrid-row.odd.bg-orange": { backgroundColor: "#fff4e5" },
            "& .MuiDataGrid-row.bg-orange:hover": {
              backgroundColor: "#ffe0b2",
            },
            "& .MuiDataGrid-row.even.bg-pink": { backgroundColor: "#f8bbd0" },
            "& .MuiDataGrid-row.odd.bg-pink": { backgroundColor: "#fce4ec" },
            "& .MuiDataGrid-row.bg-pink:hover": { backgroundColor: "#f48fb1" },
            "& .MuiDataGrid-row.even.bg-purple": {
              backgroundColor: "#d1c4e9",
            },
            "& .MuiDataGrid-row.odd.bg-purple": {
              backgroundColor: "#ede7f6",
            },
            "& .MuiDataGrid-row.bg-purple:hover": {
              backgroundColor: "#b39ddb",
            },

            "& .MuiDataGrid-row.even.bg-blue": { backgroundColor: "#d0e8ff" },
            "& .MuiDataGrid-row.odd.bg-blue": { backgroundColor: "#e3f2fd" },
            "& .MuiDataGrid-row.bg-blue:hover": { backgroundColor: "#bbdefb" },
            "& .MuiDataGrid-row.even.bg-yellow": { backgroundColor: "#fff9c4" },
            "& .MuiDataGrid-row.odd.bg-yellow": { backgroundColor: "#fffde7" },
            "& .MuiDataGrid-row.bg-yellow:hover": {
              backgroundColor: "#fff59d",
            },
            "& .MuiDataGrid-row.even.bg-light": { backgroundColor: "#f5f5f5" },
            "& .MuiDataGrid-row.odd.bg-light": { backgroundColor: "#fafafa" },
            "& .MuiDataGrid-row.bg-light:hover": { backgroundColor: "#eeeeee" },
            "& .MuiDataGrid-row.even.bg-grey": { backgroundColor: "#e0e0e0" },
            "& .MuiDataGrid-row.odd.bg-grey": { backgroundColor: "#eeeeee" },
            "& .MuiDataGrid-row.bg-grey:hover": { backgroundColor: "#bdbdbd" },
            "& .MuiDataGrid-row.even.bg-red": { backgroundColor: "#ffcdd2" },
            "& .MuiDataGrid-row.odd.bg-red": { backgroundColor: "#ffebee" },
            "& .MuiDataGrid-row.bg-red:hover": { backgroundColor: "#ef9a9a" },
          },
        },
      ],
    },
  },
});

export default mybotgpTheme;
